import { useEffect, useMemo, useState } from "react";
import { ApiError, withSessionRenewal } from "../api/client.js";
import {
  canManagePortfolio,
  liveAdminPortfolioService,
  type AdminPortfolioProject,
  type AdminPortfolioService,
} from "../api/adminPortfolioService.js";
import { useSession } from "../auth/session.js";
import { EmptyState, Tag } from "../components.js";
import { Button, Field, Input, Select } from "../components/controls.js";
import { formatDate } from "../format.js";
import { Breadcrumb } from "../shell/Shell.js";
import "./admin-portfolio.css";

const WORKFLOW_OPTIONS = [
  "trunk",
  "github-flow",
  "gitlab-flow",
  "git-flow",
  "forking",
  "release-train",
  "centralized",
  "custom",
] as const;

const CONFIDENCE_OPTIONS = ["estimation", "throughput", "hybrid"] as const;
const MODE_OPTIONS = ["scope-driven", "date-driven", "continuous"] as const;
const INVALID_TARGET_DATE =
  "targetDate must be an ISO calendar date (YYYY-MM-DD), or null to clear it";
type RequiredField =
  | "programName"
  | "programStatus"
  | "projectName"
  | "projectStatus"
  | "editName"
  | "editStatus";

function display(value: string | null): string {
  return value ?? "N/A";
}

function validTargetDate(value: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const parsed = new Date(`${value}T00:00:00Z`);
  return !Number.isNaN(parsed.getTime()) && parsed.toISOString().slice(0, 10) === value;
}

function parentLabel(project: AdminPortfolioProject, names: ReadonlyMap<string, string>): string {
  if (!project.parentProjectId) return "Standalone";
  return names.get(project.parentProjectId) ?? project.parentProjectId;
}

function declaredLine(event: AdminPortfolioProject["projectDeclared"]): string {
  const actor = event.declaredBy ?? "unknown actor";
  return `${event.method} by ${actor} at ${formatDate(event.recordedAt)}`;
}

function ProjectMappings({
  projectId,
  service,
  token,
  revision,
  pending,
  mutate,
}: {
  projectId: string;
  service: AdminPortfolioService;
  token: string;
  revision: number;
  pending: string | null;
  mutate: (label: string, fn: (token: string) => Promise<void>) => Promise<void>;
}): React.ReactNode {
  const state = service.useMappings(projectId, revision);
  const [source, setSource] = useState("github");
  const [key, setKey] = useState("");
  const [keyError, setKeyError] = useState<string | null>(null);
  return (
    <div className="admin-mappings">
      <h4>Source mappings</h4>
      {state.status === "loading" && <p className="meta">Loading mappings…</p>}
      {state.status === "error" && (
        <p role="alert" className="field-error">
          Couldn&apos;t load mappings: {state.error.message}
        </p>
      )}
      {state.status === "ready" &&
        (state.envelope.data.length === 0 ? (
          <p className="meta">No source mappings for this project.</p>
        ) : (
          <ul className="admin-mapping-list">
            {state.envelope.data.map((mapping) => (
              <li key={mapping.id}>
                <span>
                  <strong>{mapping.source}</strong> · {mapping.key}
                  <small>Added {formatDate(mapping.createdAt)}</small>
                </span>
                <Button
                  variant="danger"
                  size="sm"
                  disabled={pending !== null}
                  aria-label={`Remove ${mapping.source} ${mapping.key}`}
                  onClick={() =>
                    void mutate("Source mapping", (fresh) =>
                      service.removeMapping(fresh, projectId, mapping.id),
                    )
                  }
                >
                  Remove
                </Button>
              </li>
            ))}
          </ul>
        ))}
      <form
        className="admin-form"
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          if (!key.trim()) {
            setKeyError("This field is required.");
            return;
          }
          void mutate("Source mapping", async (fresh) => {
            await service.addMapping(fresh, projectId, { source, key: key.trim() });
            setKey("");
          });
        }}
      >
        <Field label="Mapping source">
          <Select value={source} onChange={(event) => setSource(event.target.value)}>
            <option value="github">GitHub</option>
            <option value="nexus">Nexus</option>
            <option value="ado">ADO</option>
          </Select>
        </Field>
        <Field
          label="Mapping key"
          hint="owner/name, or epic:NX-n:owner/name after adding the nexus epic:NX-n key."
          error={keyError}
        >
          <Input
            value={key}
            placeholder="Owner/repo"
            onChange={(event) => {
              setKey(event.target.value);
              setKeyError(null);
            }}
            required
          />
        </Field>
        <Button type="submit" disabled={pending !== null || !token}>
          Add mapping
        </Button>
      </form>
    </div>
  );
}

export function AdminPortfolio({
  service = liveAdminPortfolioService,
}: {
  service?: AdminPortfolioService;
} = {}): React.ReactNode {
  const { session } = useSession();
  const forbidden = session ? !canManagePortfolio(session.role) : false;

  return (
    <>
      <Breadcrumb
        items={[{ label: "Portfolio", to: "/portfolio" }, { label: "Admin portfolio" }]}
      />
      <div className="head">
        <h1>Admin portfolio</h1>
      </div>
      <div className="meta">
        Create and update programs/projects as declared events, manage profile declarations, and
        reparent projects with full provenance.
      </div>
      {forbidden ? (
        <EmptyState message="This section is admin-only. Your role cannot manage portfolio declarations." />
      ) : (
        <AdminPortfolioContent service={service} />
      )}
    </>
  );
}

function AdminPortfolioContent({ service }: { service: AdminPortfolioService }): React.ReactNode {
  const { session, onUnauthenticated } = useSession();
  const token = session?.token ?? null;

  const [revision, setRevision] = useState(0);
  const state = service.usePortfolio(revision);

  const [pending, setPending] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [mutationError, setMutationError] = useState<string | null>(null);

  const [programName, setProgramName] = useState("");
  const [programStatus, setProgramStatus] = useState("active");

  const [projectName, setProjectName] = useState("");
  const [projectStatus, setProjectStatus] = useState("active");
  const [projectParentId, setProjectParentId] = useState("");

  const [selectedProjectId, setSelectedProjectId] = useState("");
  const [editName, setEditName] = useState("");
  const [editStatus, setEditStatus] = useState("active");

  const [workflowProfile, setWorkflowProfile] = useState("");
  const [stageModel, setStageModel] = useState("");
  const [confidenceBasis, setConfidenceBasis] = useState("");
  const [deliveryMode, setDeliveryMode] = useState("");
  const [targetDate, setTargetDate] = useState("");
  const [targetDateError, setTargetDateError] = useState<string | null>(null);
  const [reparentParentId, setReparentParentId] = useState("");
  const [fieldErrors, setFieldErrors] = useState<Partial<Record<RequiredField, string>>>({});

  function setRequiredValue(
    field: RequiredField,
    value: string,
    setValue: (value: string) => void,
  ): void {
    setValue(value);
    if (value.trim()) {
      setFieldErrors((current) => (current[field] ? { ...current, [field]: undefined } : current));
    }
  }

  function validateRequired(fields: [RequiredField, string][]): boolean {
    const errors: Partial<Record<RequiredField, string>> = {};
    for (const [field, value] of fields) {
      errors[field] = value.trim() ? undefined : "This field is required.";
    }
    setFieldErrors((current) => ({ ...current, ...errors }));
    return fields.every(([field]) => !errors[field]);
  }

  const projects = state.status === "ready" ? state.envelope.data : [];
  const programs = useMemo(
    () => projects.filter((project) => project.scopeType === "program"),
    [projects],
  );
  const editableProjects = useMemo(
    () => projects.filter((project) => project.scopeType !== "program"),
    [projects],
  );
  const byId = useMemo(() => new Map(projects.map((project) => [project.id, project])), [projects]);
  const nameById = useMemo(
    () => new Map(projects.map((project) => [project.id, project.name])),
    [projects],
  );

  useEffect(() => {
    if (projects.length === 0) {
      setSelectedProjectId("");
      return;
    }
    if (!selectedProjectId || !byId.has(selectedProjectId)) {
      setSelectedProjectId(projects[0]!.id);
    }
  }, [projects, byId, selectedProjectId]);

  const selected = selectedProjectId ? (byId.get(selectedProjectId) ?? null) : null;

  useEffect(() => {
    if (!selected) return;
    setEditName(selected.name);
    setEditStatus(selected.status);
    setWorkflowProfile(selected.profile.workflowProfile ?? "");
    setStageModel(selected.profile.stageModel ?? "");
    setConfidenceBasis(selected.profile.confidenceBasis ?? "");
    setDeliveryMode(selected.profile.deliveryMode ?? "");
    setTargetDate(selected.profile.targetDate ?? "");
    setTargetDateError(null);
    setReparentParentId(selected.parentProjectId ?? "");
  }, [selected]);

  async function mutate(label: string, fn: (token: string) => Promise<void>): Promise<void> {
    if (!token) return;
    setPending(label);
    setNotice(null);
    setMutationError(null);
    try {
      // NX-3433: a 401 renews the session and retries this same action once.
      await withSessionRenewal(token, onUnauthenticated, fn);
      setRevision((value) => value + 1);
      setNotice(`${label} saved.`);
    } catch (caught: unknown) {
      const apiError =
        caught instanceof ApiError ? caught : new ApiError(0, "unknown", "unexpected error");
      if (apiError.isUnauthenticated) return; // renewal failed: the session has ended
      if (label === "Target date" && apiError.code === "invalid_target_date") {
        setTargetDateError(apiError.message);
      } else {
        setMutationError(apiError.message);
      }
    } finally {
      setPending(null);
    }
  }

  if (!token) {
    return <EmptyState message="No authenticated session is available." />;
  }

  if (state.status === "loading") {
    return <div className="meta">Loading declared portfolio state…</div>;
  }

  if (state.status === "error") {
    return (
      <EmptyState
        message={
          state.error.status === 403
            ? "This section is admin-only."
            : `Couldn't load admin portfolio state: ${state.error.message}`
        }
      />
    );
  }

  return (
    <>
      <div className="admin-grid">
        <section className="panel pad">
          <h3 className="sec">Create program</h3>
          <form
            className="admin-form"
            noValidate
            onSubmit={(event) => {
              event.preventDefault();
              if (
                !validateRequired([
                  ["programName", programName],
                  ["programStatus", programStatus],
                ])
              )
                return;
              void mutate("Program", async (fresh) => {
                await service.createProgram(fresh, {
                  name: programName.trim(),
                  status: programStatus.trim(),
                });
                setProgramName("");
                setProgramStatus("active");
              });
            }}
          >
            <Field label="Name" error={fieldErrors.programName}>
              <Input
                value={programName}
                onChange={(event) =>
                  setRequiredValue("programName", event.target.value, setProgramName)
                }
                required
              />
            </Field>
            <Field label="Status" error={fieldErrors.programStatus}>
              <Input
                value={programStatus}
                onChange={(event) =>
                  setRequiredValue("programStatus", event.target.value, setProgramStatus)
                }
                required
              />
            </Field>
            <Button type="submit" disabled={pending !== null}>
              Create program
            </Button>
          </form>
        </section>

        <section className="panel pad">
          <h3 className="sec">Create project</h3>
          <form
            className="admin-form"
            noValidate
            onSubmit={(event) => {
              event.preventDefault();
              if (
                !validateRequired([
                  ["projectName", projectName],
                  ["projectStatus", projectStatus],
                ])
              )
                return;
              void mutate("Project", async (fresh) => {
                await service.createProject(fresh, {
                  name: projectName.trim(),
                  status: projectStatus.trim(),
                  parentProjectId: projectParentId === "" ? null : projectParentId,
                });
                setProjectName("");
                setProjectStatus("active");
                setProjectParentId("");
              });
            }}
          >
            <Field label="Name" error={fieldErrors.projectName}>
              <Input
                value={projectName}
                onChange={(event) =>
                  setRequiredValue("projectName", event.target.value, setProjectName)
                }
                required
              />
            </Field>
            <Field label="Status" error={fieldErrors.projectStatus}>
              <Input
                value={projectStatus}
                onChange={(event) =>
                  setRequiredValue("projectStatus", event.target.value, setProjectStatus)
                }
                required
              />
            </Field>
            <Field label="Parent program">
              <Select
                value={projectParentId}
                onChange={(event) => setProjectParentId(event.target.value)}
              >
                <option value="">Standalone</option>
                {programs.map((program) => (
                  <option key={program.id} value={program.id}>
                    {program.name}
                  </option>
                ))}
              </Select>
            </Field>
            <Button type="submit" disabled={pending !== null}>
              Create project
            </Button>
          </form>
        </section>
      </div>

      <section className="panel pad">
        <h3 className="sec">Update declarations</h3>
        {projects.length === 0 ? (
          <EmptyState message="No programs or projects are declared yet." />
        ) : (
          <div className="admin-grid admin-declarations-grid">
            <form
              className="admin-form"
              noValidate
              onSubmit={(event) => {
                event.preventDefault();
                if (!selected) return;
                if (
                  !validateRequired([
                    ["editName", editName],
                    ["editStatus", editStatus],
                  ])
                )
                  return;
                void mutate("Project metadata", async (fresh) => {
                  await service.updateProject(fresh, selected.id, {
                    name: editName.trim(),
                    status: editStatus.trim(),
                  });
                });
              }}
            >
              <Field label="Selection">
                <Select
                  value={selectedProjectId}
                  onChange={(event) => {
                    setSelectedProjectId(event.target.value);
                    setFieldErrors((current) => ({
                      ...current,
                      editName: undefined,
                      editStatus: undefined,
                    }));
                  }}
                >
                  {projects.map((project) => (
                    <option key={project.id} value={project.id}>
                      {project.name} ({project.scopeType})
                    </option>
                  ))}
                </Select>
              </Field>
              <Field label="Name" error={fieldErrors.editName}>
                <Input
                  value={editName}
                  onChange={(event) =>
                    setRequiredValue("editName", event.target.value, setEditName)
                  }
                  required
                />
              </Field>
              <Field label="Status" error={fieldErrors.editStatus}>
                <Input
                  value={editStatus}
                  onChange={(event) =>
                    setRequiredValue("editStatus", event.target.value, setEditStatus)
                  }
                  required
                />
              </Field>
              <Button type="submit" disabled={pending !== null || !selected}>
                Save name/status
              </Button>
            </form>

            {selected && selected.scopeType !== "program" ? (
              <>
                <form
                  className="admin-form"
                  onSubmit={(event) => {
                    event.preventDefault();
                    void mutate("Project profile", async (fresh) => {
                      await service.declareProfile(fresh, selected.id, {
                        workflowProfile: workflowProfile === "" ? null : workflowProfile,
                        stageModel: stageModel.trim() === "" ? null : stageModel.trim(),
                        confidenceBasis: confidenceBasis === "" ? null : confidenceBasis,
                        deliveryMode: deliveryMode === "" ? null : deliveryMode,
                      });
                    });
                  }}
                >
                  <Field label="Workflow profile">
                    <Select
                      value={workflowProfile}
                      onChange={(event) => setWorkflowProfile(event.target.value)}
                    >
                      <option value="">N/A</option>
                      {WORKFLOW_OPTIONS.map((option) => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                    </Select>
                  </Field>
                  <Field label="Stage model">
                    <Input
                      value={stageModel}
                      onChange={(event) => setStageModel(event.target.value)}
                    />
                  </Field>
                  <Field label="Confidence basis">
                    <Select
                      value={confidenceBasis}
                      onChange={(event) => setConfidenceBasis(event.target.value)}
                    >
                      <option value="">N/A</option>
                      {CONFIDENCE_OPTIONS.map((option) => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                    </Select>
                  </Field>
                  <Field label="Delivery mode">
                    <Select
                      value={deliveryMode}
                      onChange={(event) => setDeliveryMode(event.target.value)}
                    >
                      <option value="">N/A</option>
                      {MODE_OPTIONS.map((option) => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                    </Select>
                  </Field>
                  <Button type="submit" disabled={pending !== null}>
                    Declare profile
                  </Button>
                </form>

                <form
                  className="admin-form"
                  noValidate
                  onSubmit={(event) => {
                    event.preventDefault();
                    const value = targetDate.trim();
                    if (value !== "" && !validTargetDate(value)) {
                      setTargetDateError(INVALID_TARGET_DATE);
                      return;
                    }
                    setTargetDateError(null);
                    void mutate("Target date", async (fresh) => {
                      await service.setTargetDate(fresh, selected.id, value === "" ? null : value);
                    });
                  }}
                >
                  <Field
                    label="Target date"
                    hint="YYYY-MM-DD; leave empty to clear."
                    error={targetDateError}
                  >
                    <Input
                      type="text"
                      inputMode="numeric"
                      placeholder="YYYY-MM-DD"
                      value={targetDate}
                      onChange={(event) => {
                        setTargetDate(event.target.value);
                        setTargetDateError(null);
                      }}
                    />
                  </Field>
                  <Button type="submit" disabled={pending !== null}>
                    Save target date
                  </Button>
                </form>

                <form
                  className="admin-form"
                  onSubmit={(event) => {
                    event.preventDefault();
                    void mutate("Project parent", async (fresh) => {
                      await service.reparentProject(
                        fresh,
                        selected.id,
                        reparentParentId === "" ? null : reparentParentId,
                      );
                    });
                  }}
                >
                  <Field label="Parent program">
                    <Select
                      value={reparentParentId}
                      onChange={(event) => setReparentParentId(event.target.value)}
                    >
                      <option value="">Standalone</option>
                      {programs.map((program) => (
                        <option key={program.id} value={program.id}>
                          {program.name}
                        </option>
                      ))}
                    </Select>
                  </Field>
                  <Button type="submit" disabled={pending !== null}>
                    Reparent project
                  </Button>
                </form>
                <ProjectMappings
                  key={selected.id}
                  projectId={selected.id}
                  service={service}
                  token={token}
                  revision={revision}
                  pending={pending}
                  mutate={mutate}
                />
              </>
            ) : (
              <div className="meta">
                Profile and reparent actions apply to projects (not programs).
              </div>
            )}
          </div>
        )}
      </section>

      {notice && <div className="meta">{notice}</div>}
      {mutationError && <EmptyState message={mutationError} />}

      <section className="panel pad">
        <h3 className="sec">Declared programs and projects</h3>
        {projects.length === 0 ? (
          <EmptyState message="No programs or projects are declared yet." />
        ) : (
          <div className="admin-list">
            {projects.map((project) => (
              <article key={project.id} className="admin-item">
                <div className="admin-item-head">
                  <div>
                    <strong>{project.name}</strong>
                    <div className="meta">
                      {project.scopeType} · status {project.status} · parent{" "}
                      {parentLabel(project, nameById)}
                    </div>
                  </div>
                  <Tag>{project.scopeType}</Tag>
                </div>
                <div className="admin-profile">
                  <span>workflow {display(project.profile.workflowProfile)}</span>
                  <span>stage {display(project.profile.stageModel)}</span>
                  <span>basis {display(project.profile.confidenceBasis)}</span>
                  <span>mode {display(project.profile.deliveryMode)}</span>
                </div>
                <div className="meta">
                  Project declaration: {declaredLine(project.projectDeclared)}
                </div>
                <div className="meta">
                  Profile declaration:{" "}
                  {project.profileDeclared ? declaredLine(project.profileDeclared) : "N/A"}
                </div>
              </article>
            ))}
          </div>
        )}
      </section>

      {editableProjects.length === 0 && projects.length > 0 && (
        <div className="meta">
          No non-program projects are available for profile/reparent actions yet.
        </div>
      )}
    </>
  );
}
